import { html, LitElement, PropertyValues, TemplateResult } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { ChipColor, ChipEvents, GzChipsItem } from '../types';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { ButtonColor, FontSize } from '../../gzIconButton/types';

export class GzChipItemWebComponent extends LitElement implements GzChipsItem {
  @property({ type: String, attribute: true, reflect: true })
  text;

  @property({ type: String, attribute: true, reflect: true })
  boldSubtext;

  @property({ type: String, attribute: true, reflect: true })
  icon;

  @property({ type: String, attribute: true, reflect: true })
  iconSize: FontSize = FontSize.ICON_SIZE_18;

  @property({ type: Boolean, attribute: true, reflect: true })
  disabled = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  removable = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  readonly = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  notSelectable = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  private selected = false;

  @state()
  private ripple = false;

  @property({ type: String, attribute: true, reflect: true })
  color: ChipColor = ChipColor.DEFAULT;

  private readonly removeIcon = 'ds-icon-delete_large';
  private readonly rippleTimeoutInMs = 401;

  @query('.chip-item-container') container: HTMLElement;

  static get styles() {
    return styles;
  }

  protected updated(changedProperties: PropertyValues): void {
    if (changedProperties.has('readonly') && this.readonly) {
      this.selected = false;
    }
  }

  private setSelected() {
    this.blur();
    if (this.readonly) {
      return;
    }

    this.selected = !this.notSelectable && !this.selected;
    this.ripple = true;

    setTimeout(() => {
      this.ripple = false;
    }, this.rippleTimeoutInMs);
  }

  render(): TemplateResult {
    return html`
      <div class="chip-item-container" tabindex="0" @click="${this.setSelected}">
        ${this.renderThumbnail()}
        <gz-tooltip tooltipText="${this.text}" isEllipsis class="content"> ${this.renderText()} </gz-tooltip>
        ${this.removable && !this.readonly ? this.renderStaticIcon(this.removeIcon, this.removeChip) : ''}
        ${this.renderRipple()}
      </div>
    `;
  }

  private renderText(): TemplateResult {
    if (!this.boldSubtext) {
      return html`${this.text}`;
    }

    const lowerText = this.text.toLowerCase();
    const lowerBoldSubtext = this.boldSubtext.toLowerCase();
    const parts: (string | TemplateResult)[] = [];
    let currentIndex = 0;

    while (currentIndex < this.text.length) {
      const startIndex = lowerText.indexOf(lowerBoldSubtext, currentIndex);
      if (startIndex === -1) {
        parts.push(this.text.substring(currentIndex));
        break;
      }

      if (startIndex > currentIndex) {
        parts.push(this.text.substring(currentIndex, startIndex));
      }
      parts.push(html`<b>${this.text.substring(startIndex, startIndex + this.boldSubtext.length)}</b>`);
      currentIndex = startIndex + this.boldSubtext.length;
    }

    return html`${parts.map(part => part)}`;
  }

  private renderRipple(): TemplateResult | undefined {
    if (!this.ripple) {
      return;
    }

    return html`<span class="ripple"></span>`;
  }

  private renderStaticIcon(iconName: string, callback?: (event: Event) => void): TemplateResult {
    if (callback) {
      return html`<gz-icon
        class="icon"
        name="${iconName}"
        @click="${callback}"
        size="${this.iconSize}"
        color="${ButtonColor.SECONDARY}"
      ></gz-icon>`;
    }

    return html`<gz-icon
      class="icon"
      name="${iconName}"
      size="${this.iconSize}"
      color="${ButtonColor.SECONDARY}"
    ></gz-icon>`;
  }

  private renderThumbnail(): TemplateResult | undefined {
    if (isEmpty(this.icon)) {
      return;
    }

    return html`<gz-icon
      class="icon thumbnail"
      name="${this.icon}"
      size="${this.iconSize}"
      color="${ButtonColor.SECONDARY}"
    ></gz-icon>`;
  }

  private removeChip(): void {
    const eventRemove = new CustomEvent(ChipEvents.REMOVE, {
      detail: {
        currentTarget: this.container,
      },
    });

    this.dispatchEvent(eventRemove);
  }
}
